import { useState } from 'react';
import { Activity, TrendingDown, TrendingUp } from 'lucide-react';
import { ErrorState, Skeleton } from '@/components/States';
import { useSenderHealth } from '@/hooks/use-sender-health';
import { MAX_SCORE, type SenderHealthPoint } from '@/lib/sender-health-api';
import { formatDateTime } from '@/lib/utils';

/**
 * The account's sending reputation over time (spec § 4.6).
 *
 * ── WHY A TREND AND NOT A NUMBER ──────────────────────────────────────────────────
 *
 * A reputation problem is a slope. "We were 4.2 in April and we are 2.8 now" is the sentence somebody
 * acts on; today's score alone is not actionable, because 3.1 could be a recovery or the start of a
 * slide. So the change is the headline and the daily figure sits beside it.
 *
 * It is also why the snapshots exist at all: SendGrid retains 90 days, and a slide that began earlier
 * than that is unsayable from the provider's own console.
 *
 * ── A DAY WITH NO SCORE IS DRAWN AS A GAP ─────────────────────────────────────────
 *
 * SendGrid scores a day only where open tracking was on and the account sent 1,000+ messages in the
 * prior 30 days, so a quiet week legitimately has none. Three tempting shortcuts all misrepresent it,
 * and the chart avoids each deliberately:
 *
 *   * Joining across a gap draws a line over days nothing was measured on.
 *   * Rendering a gap as zero reads as "as bad as it can get" on a 1–5 scale — the opposite of quiet.
 *   * Dropping the day entirely compresses the axis so a fortnight's silence looks like a day's.
 *
 * So each day is its own column, an unscored one renders as an empty track, and the count of scored
 * days is stated next to the change as its honest denominator.
 */
export function DeliverabilityPage() {
  const [days, setDays] = useState(90);
  const trend = useSenderHealth(days);

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">Deliverability</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            SendGrid’s engagement quality score for the whole account, recorded once a night. It rates
            the account rather than any single brand or from-address, so one brand’s mail affects every
            brand’s number.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-muted">
          Window
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>A year</option>
          </select>
        </label>
      </div>

      {trend.isPending ? (
        <div className="mt-5 flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
        </div>
      ) : trend.isError ? (
        <div className="mt-5">
          <ErrorState error={trend.error} onRetry={() => void trend.refetch()} />
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-5">
          <Headline
            latest={trend.data.latest}
            change={trend.data.change}
            scoredDays={trend.data.scoredDays}
            requestedDays={trend.data.requestedDays}
          />

          {trend.data.scoredDays === 0 ? (
            <NothingScoredYet requestedDays={trend.data.requestedDays} />
          ) : (
            <>
              <TrendChart points={trend.data.points} />
              <Components latest={trend.data.latest} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Headline({
  latest,
  change,
  scoredDays,
  requestedDays,
}: {
  latest: SenderHealthPoint | null;
  change: number | null;
  scoredDays: number;
  requestedDays: number;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            Latest score
          </p>

          <p className="mt-0.5 text-3xl font-semibold tabular-nums text-foreground">
            {latest?.score === null || latest === null ? (
              <span className="text-xl font-normal text-muted">Not scored</span>
            ) : (
              <>
                {latest.score.toFixed(1)}
                <span className="ml-1 text-base font-normal text-muted">of {MAX_SCORE}</span>
              </>
            )}
          </p>

          {latest !== null && (
            <p className="mt-0.5 text-xs text-muted">
              {/* The day, because it is often not yesterday. */}
              For {latest.day} · read {formatDateTime(latest.fetchedAt)}
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            Change across this window
          </p>

          <p className="mt-0.5 flex items-center gap-1.5 text-2xl font-semibold tabular-nums">
            <Direction change={change} />
          </p>

          {/* The honest denominator: "down 1.2 over 4 scored days out of 90" is a different claim
              from "down 1.2 over 90 days", and only one of them is true. */}
          <p className="mt-0.5 text-xs text-muted">
            {scoredDays} of {requestedDays} days carry a score
          </p>
        </div>
      </div>

      {latest !== null && latest.score !== null && latest.score <= 2 && (
        <p role="status" className="mt-3 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-ink">
          At or below 2 of {MAX_SCORE}, SendGrid treats the account as needing attention. A score this
          low tends to precede deliverability trouble rather than report it — the components below say
          which part is dragging.
        </p>
      )}
    </section>
  );
}

/** Colour is never the only signal — each state carries a word and an icon as well as a hue. */
function Direction({ change }: { change: number | null }) {
  if (change === null) {
    return (
      <span className="text-xl font-normal text-muted">
        Not enough scored days
      </span>
    );
  }

  if (change === 0) {
    return <span className="text-foreground">Flat</span>;
  }

  const isFalling = change < 0;

  return (
    <span className={isFalling ? 'flex items-center gap-1.5 text-danger-ink' : 'flex items-center gap-1.5 text-success-ink'}>
      {isFalling ? (
        <TrendingDown className="size-5" aria-hidden="true" />
      ) : (
        <TrendingUp className="size-5" aria-hidden="true" />
      )}
      {change > 0 ? '+' : ''}
      {change.toFixed(1)}
      <span className="text-sm font-normal">{isFalling ? 'worse' : 'better'}</span>
    </span>
  );
}

/**
 * One column per day, oldest on the left.
 *
 * Deliberately not a line chart. A line has to decide what to do between two scored days with a gap
 * between them, and every answer is a claim about days nobody measured. Columns make no such claim: an
 * unscored day is simply an empty track.
 */
function TrendChart({ points }: { points: SenderHealthPoint[] }) {
  // The API returns newest first; a chart reads left to right in time.
  const ordered = [...points].reverse();

  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <h2 className="text-sm font-semibold text-heading">Daily score</h2>

      <p className="mt-0.5 text-xs text-muted">
        An empty column is a day SendGrid did not score, usually because volume was below its threshold.
        It is left blank rather than joined up or drawn as zero.
      </p>

      <div className="mt-4 flex h-40 items-end gap-px overflow-x-auto" role="img"
        aria-label={`Engagement quality per day. ${ordered.filter((point) => point.score !== null).length} of ${ordered.length} days scored.`}
      >
        {ordered.map((point) => (
          <div key={point.day} className="flex h-full min-w-1 flex-1 flex-col justify-end">
            {/* An unscored day is a baseline TICK, not a short bar and not a faint full-height one.
                A stub too faint to see reads as the chart having ended; a full-height ghost reads as a
                high score rendered oddly. A tick on the axis is the conventional "this day exists and
                has no reading", and it cannot be mistaken for a value. */}
            <div
              className={
                point.score === null
                  ? 'w-full bg-border'
                  : point.score <= 2
                    ? 'w-full rounded-t-sm bg-danger'
                    : point.score <= 3.5
                      ? 'w-full rounded-t-sm bg-warning'
                      : 'w-full rounded-t-sm bg-success'
              }
              style={{ height: point.score === null ? '4px' : `${(point.score / MAX_SCORE) * 100}%` }}
              title={point.score === null ? `${point.day}: not scored` : `${point.day}: ${point.score.toFixed(1)} of ${MAX_SCORE}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-2 flex justify-between text-xs text-muted">
        <span>{ordered[0]?.day}</span>
        <span>{ordered[ordered.length - 1]?.day}</span>
      </div>
    </section>
  );
}

/**
 * What SendGrid says the score is made of.
 *
 * Worth showing because the headline is not actionable on its own: "3.1" tells you to worry, and the
 * components tell you whether to fix the list or the content.
 */
function Components({ latest }: { latest: SenderHealthPoint | null }) {
  if (latest === null) return null;

  const rows: { label: string; value: number | null; hint: string }[] = [
    {
      label: 'Engagement recency',
      value: latest.engagementRecency,
      hint: 'How recently recipients last engaged. Falls when a list ages without being cleaned.',
    },
    {
      label: 'Open rate',
      value: latest.openRate,
      hint: 'Against comparable senders, not against your own history.',
    },
    {
      label: 'Bounce classification',
      value: latest.bounceClassification,
      hint: 'What KIND of bounces — invalid addresses score worse than full mailboxes.',
    },
    { label: 'Bounce rate', value: latest.bounceRate, hint: 'How many, as a share of what was sent.' },
    {
      label: 'Spam rate',
      value: latest.spamRate,
      hint: 'Complaints. The component that damages a reputation fastest.',
    },
  ];

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-heading">
        What the score is made of
      </h2>

      <dl className="divide-y divide-border">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-wrap items-baseline gap-x-4 px-4 py-3">
            <dt className="min-w-48 flex-1 text-sm text-foreground">
              {row.label}
              <span className="mt-0.5 block text-xs text-muted">{row.hint}</span>
            </dt>

            <dd className="text-lg font-semibold tabular-nums text-foreground">
              {row.value === null ? (
                <span className="text-sm font-normal text-muted">Not scored</span>
              ) : (
                <>
                  {row.value.toFixed(1)}
                  <span className="ml-1 text-xs font-normal text-muted">/ {MAX_SCORE}</span>
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The empty state, and it needs to explain rather than apologise: on a new or quiet account this is
 * the correct and expected answer, not a fault.
 */
function NothingScoredYet({ requestedDays }: { requestedDays: number }) {
  return (
    <section className="rounded-lg border border-border bg-surface p-6 text-center">
      <Activity className="mx-auto size-6 text-muted" aria-hidden="true" />

      <h2 className="mt-2 text-sm font-semibold text-heading">No score for the last {requestedDays} days</h2>

      <p className="mx-auto mt-1 max-w-xl text-sm text-muted">
        This is expected rather than broken. SendGrid only scores a day when open tracking is on and the
        account sent at least 1,000 messages in the previous 30 days, so a new or quiet account has no
        score at all. The snapshot runs every night, so a figure will appear once volume clears that
        threshold.
      </p>
    </section>
  );
}
